<template>
  <div class="login-title">{{ t('Create your account') }}</div>
  <register-step :step="1" :count="3" />
  <div class="row-2-fields">
    <ui-input
      class="ui-input__login"
      v-model="form.firstName"
      :label="t('First name')"
      :placeholder="t('First name')"
      autofocus
      name="firstName"
      autocomplete="given-name"
      @blur="validateFirstName(form.firstName)"
      @focus="clearFieldError('firstName')"
      :error-messages="fieldErrors.firstName ? [fieldErrors.firstName] : []"
    />
    <ui-input
      class="ui-input__login"
      v-model="form.lastName"
      :label="t('Last name')"
      :placeholder="t('Last name')"
      type="text"
      name="lastName"
      autocomplete="family-name"
      :error-messages="fieldErrors.lastName ? [fieldErrors.lastName] : []"
      @blur="validateLastName(form.lastName)"
      @focus="clearFieldError('lastName')"
    />
  </div>
  <ui-input
    v-model="form.email"
    class="ui-input__login"
    :label="t('Email')"
    :placeholder="t('Email')"
    type="email"
    name="email"
    @blur="validateEmail(form.email)"
    @focus="clearFieldError('email')"
    autocomplete="email"
    :error-messages="fieldErrors.email ? [fieldErrors.email] : []"
  />
  <ui-input
    class="ui-input__login"
    :label="t('Password')"
    :placeholder="t('Password')"
    autocomplete="new-password"
    v-model="form.password"
    name="password"
    type="password"
    @keyup.enter="submit"
    :error-messages="fieldErrors.password ? [fieldErrors.password] : []"
    @blur="validatePassword(form.password)"
    @focus="clearFieldError('password')"
  />
  <ui-input
    v-model="form.promoCode"
    class="ui-input__login"
    :label="t('Promo code')"
    :placeholder="t('If you have')"
    type="text"
    name="promoCode"
  />

  <p
    class="terms-text"
    v-html="
      t(
        `By signing up, I agree with <a href='https://bemob.com/terms-and-conditions' target='_blank'>terms and conditions</a> of BeMob`,
      )
    "
  />
  <div class="login-recaptcha">
    <RecaptchaV2
      @expired-callback="onRecaptchaExpired"
      @load-callback="onRecaptchaVerify"
    />
  </div>
  <ui-button
    :disabled="disabledBtn"
    :loading="apiLoading"
    class="w100"
    button-style="contained"
    @click="submit"
    :text="t('Create Account')"
    iconAppend="fas fa-arrow-right"
  />
</template>

<script setup lang="ts">
import { reactive, computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { RecaptchaV2 } from 'vue3-recaptcha-v2';
import RegisterStep from '@/views/pages/components/register-step.vue';
import { useI18n } from '@/composables';
import {
  registerApiRequest,
  validateFieldApiRequest,
} from '@/services/api/auth/auth-register-api';
import { loginApiRequest } from '@/services/api/auth/auth-login-api';
import { setAuthToken } from '@/services/api/main/axios';
import i18n from '@/i18n';
import cookies from '@/utils/cookies';
import type { FieldErrors } from '@/services/models';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const apiLoading = ref(false);
const serverFieldErrors = reactive<FieldErrors>({});
const fieldErrors = reactive<Record<string, string>>({});
const recaptchaResponse = ref('');

const onRecaptchaVerify = (response: string) => {
  recaptchaResponse.value = response;
};

const onRecaptchaExpired = () => {
  recaptchaResponse.value = '';
};

const clearFieldError = (field: string) => {
  delete fieldErrors[field];
};

const validateFirstName = (firstName: string): boolean => {
  if (!firstName) {
    fieldErrors.firstName = t('{0} is required', [t('First name')]);
    return false;
  }
  if (firstName.length < 2 || firstName.length > 75) {
    fieldErrors.firstName = t('Should be at least 2 and less than 75 letters');
    return false;
  }
  return true;
};

const validateLastName = (lastName: string): boolean => {
  if (!lastName) {
    fieldErrors.lastName = t('{0} is required', [t('Last name')]);
    return false;
  }
  if (lastName.length < 2 || lastName.length > 75) {
    fieldErrors.lastName = t('Should be at least 2 and less than 75 letters');
    return false;
  }
  return true;
};

const validatePassword = (password: string): boolean => {
  if (!password) {
    fieldErrors.password = t('{0} is required', [t('Password')] as unknown as Record<
      string,
      unknown
    >);
    return false;
  }
  if (password.length < 8) {
    fieldErrors.password = t('Field "{0}" must contain at least 8 chars', [
      t('Password'),
    ] as unknown as Record<string, unknown>);
    return false;
  }
  return true;
};

const validateEmail = async (email: string): Promise<boolean> => {
  if (!email) {
    fieldErrors.email = t('{0} is required', [t('Email')]);
    return false;
  }

  try {
    const [emailUniquenessValidation, emailFormatValidation] = await Promise.all([
      validateFieldApiRequest('user', 'email-free', email),
      validateFieldApiRequest('user', 'email-valid', email),
    ]);

    if (!emailUniquenessValidation.data.isValid) {
      fieldErrors.email = t('Account with this email already registered');
      return false;
    }
    if (!emailFormatValidation.data.isValid) {
      fieldErrors.email = t('Field "{0}" should be a valid email address', [t('Email')]);
      return false;
    }
  } catch {
    // ignore validation errors silently
  }

  return true;
};

const apiSubmit = async (regForm: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  promoCode: string;
}) => {
  apiLoading.value = true;

  const trackingId = (route.query.trackingId as string) || '';
  const referredBy = cookies.get('referralId') || '';

  try {
    await registerApiRequest({
      ...regForm,
      recaptchaResponse: recaptchaResponse.value,
      trackingId,
      referredBy,
      language: i18n.global.locale.value as string,
    });

    try {
      const loginRes = await loginApiRequest({
        email: regForm.email,
        password: regForm.password,
        rememberMe: true,
      });
      setAuthToken(loginRes.data.payload.id);

      if (loginRes.data.payload.ttl === 0) {
        router.push('/two-factor');
      } else {
        router.push('/register-email-verification');
      }
    } catch {
      router.push('/login');
    }
  } catch (err: unknown) {
    const errors = (err as { response?: { data?: { errors?: FieldErrors } } })?.response
      ?.data?.errors;
    if (errors && typeof errors === 'object') {
      Object.keys(errors).forEach(field => {
        serverFieldErrors[field] = errors[field];
      });
    }
  } finally {
    apiLoading.value = false;
  }
};

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  promoCode: '',
});

onMounted(() => {
  form.promoCode = (route.query.promo as string) || '';
});

const disabledBtn = computed(
  () =>
    !form.firstName ||
    !form.lastName ||
    !form.email ||
    !form.password ||
    !recaptchaResponse.value ||
    Object.values(fieldErrors).some(item => !!item),
);

const submit = () => apiSubmit(form);
</script>

<style lang="scss" scoped>
.terms-text {
  font-weight: 400;
}
</style>
